import {
    FC,
    FormEvent,
    ReactElement,
    useRef,
    useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';

import { ErrorLabel } from '../../../components';
import { Button, RecaptchaDisclaimer, TextInput } from '../../../compositions';
import { FormProps } from '../../../entities/@forms/Form/Form';
import { RequestResetPasswordFormData } from '../../../entities/@forms/RequestResetPasswordForm/RequestResetPasswordForm';
import { AppRoute, appRoutes } from '../../../entities/Routing/Routing';
import { scrollIntoViewIncludingNavigation } from '../../../helpers/scroll';
import { useTrans } from '../../../hooks';
import { RequestResetPasswordFormErrors, validateRequestResetPasswordFormData } from './validations';

import './RequestResetPasswordForm.scss';

interface RequestResetPasswordFormProps extends FormProps<RequestResetPasswordFormData> {
    className?: string;
}

export const RequestResetPasswordForm: FC<RequestResetPasswordFormProps> = ({
    isLoading,
    error,
    onSubmit,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formRef = useRef<HTMLFormElement>(null);
    const [formErrors, setFormErrors] = useState<RequestResetPasswordFormErrors>({});
    const [botDetected, setBotDetected] = useState<boolean>(false);

    const [username, setUsername] = useState<string>('');

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!executeRecaptcha) {
            setBotDetected(true);
            return;
        }

        const formData: RequestResetPasswordFormData = { username };

        const [errors, hasErrors] = validateRequestResetPasswordFormData(formData);
        const token = await executeRecaptcha();

        setFormErrors(errors);
        setBotDetected(!token);

        if (!hasErrors) {
            onSubmit(formData);
        } else {
            scrollIntoViewIncludingNavigation<HTMLFormElement>(formRef);
        }
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`request-reset-password-form ${className}`}
        >
            <h1 className="request-reset-password-form__heading">
                {trans('containers.requestResetPasswordForm.heading')}
            </h1>

            <p className="request-reset-password-form__instructions">
                {trans('containers.requestResetPasswordForm.instructions')}
            </p>

            <TextInput
                type="email"
                inputMode="email"
                autoComplete="email"
                label={trans('containers.requestResetPasswordForm.emailLabel')}
                placeholder={trans('containers.requestResetPasswordForm.emailPlaceholder')}
                value={username}
                error={formErrors.username}
                disabled={isLoading}
                onChange={setUsername}
                className="request-reset-password-form__username-input"
            />

            <Button
                hasAnimation
                type="submit"
                disabled={isLoading}
                text={trans('containers.requestResetPasswordForm.submitButton')}
                className="request-reset-password-form__submit-button"
            />

            <RecaptchaDisclaimer className="request-reset-password-form__recaptcha-disclaimer" />

            {botDetected && (
                <ErrorLabel
                    text={trans('errors.unknownError')}
                    className="request-reset-password-form__error-label"
                />
            )}

            {error && (
                <ErrorLabel
                    text={error}
                    className="request-reset-password-form__error-label"
                />
            )}

            <Link
                to={trans(appRoutes[AppRoute.login].path)}
                className="request-reset-password-form__back-to-login-button"
            >
                {trans('containers.requestResetPasswordForm.backToLoginButton')}
            </Link>
        </form>
    );
};
